import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useBusiness } from '../contexts/BusinessContext';

export const usePrinters = () => {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { business } = useBusiness();

  const fetchPrinters = async () => {
    if (!business?.id) {
      console.log('No business ID available:', business);
      return;
    }

    try {
      console.log('Fetching printers for business:', business.id);
      setLoading(true);
      const { data, error: supabaseError } = await supabase
        .from('printers')
        .select('*')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      if (supabaseError) {
        console.error('Supabase error fetching printers:', supabaseError);
        throw supabaseError;
      }
      
      console.log('Printers fetched successfully:', data);
      setPrinters(data || []);
    } catch (err) {
      console.error('Error fetching printers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addPrinter = async (printerData) => {
    try {
      const { data, error: supabaseError } = await supabase
        .from('printers')
        .insert([{
          ...printerData,
          business_id: business.id,
          status: 'offline',
          last_seen: new Date().toISOString()
        }])
        .select()
        .single();

      if (supabaseError) throw supabaseError;
      setPrinters(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error adding printer:', err);
      throw err;
    }
  };

  const updatePrinter = async (printerId, updates) => {
    try {
      const { data, error: supabaseError } = await supabase
        .from('printers')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', printerId)
        .eq('business_id', business.id) // Seguridad adicional
        .select()
        .single();

      if (supabaseError) throw supabaseError;
      setPrinters(prev => prev.map(p => p.id === printerId ? data : p));
      return data;
    } catch (err) {
      console.error('Error updating printer:', err);
      throw err;
    }
  };

  const deletePrinter = async (printerId) => {
    try {
      const { error: supabaseError } = await supabase
        .from('printers')
        .delete()
        .eq('id', printerId)
        .eq('business_id', business.id); // Seguridad adicional

      if (supabaseError) throw supabaseError;
      setPrinters(prev => prev.filter(p => p.id !== printerId));
    } catch (err) {
      console.error('Error deleting printer:', err);
      throw err;
    }
  };

  const updatePrinterStatus = async (printerId, isConnected) => {
    try {
      console.log('Actualizando estado de impresora:', {
        printer_id: printerId,
        status: isConnected ? 'online' : 'offline',
        last_seen: new Date().toISOString()
      });

      const { data, error: supabaseError } = await supabase
        .from('printers')
        .update({
          status: isConnected ? 'online' : 'offline',
          last_seen: new Date().toISOString()
        })
        .eq('id', printerId)
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      // Actualizar el estado local
      setPrinters(prev => prev.map(printer => 
        printer.id === printerId ? { ...printer, status: isConnected ? 'online' : 'offline' } : printer
      ));

      return data;
    } catch (err) {
      console.error('Error actualizando estado de impresora:', err);
      throw err;
    }
  };

  // Cargar impresoras cuando cambie el business
  useEffect(() => {
    fetchPrinters();
  }, [business?.id]);

  return {
    printers,
    loading,
    error,
    addPrinter,
    updatePrinter,
    deletePrinter,
    updatePrinterStatus,
    refreshPrinters: fetchPrinters
  };
}; 